import React, { useEffect, useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Loader2, Save, Send, Eye, EyeOff, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useAdminAuth } from './AdminAuthContext';
import { adminApi } from './adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAdminOtpGuard } from './useAdminOtpGuard';

/**
 * SuperAdmin WhatsApp delivery configuration.
 * Currently supports a single provider — MSG91 — but the panel is built
 * around a `provider` switch so future providers (Twilio, Meta Cloud) drop
 * in without restructuring the UI.
 *
 * Mirrors the AdminEmailIntegrationsPage pattern:
 *   - `is_auth_key_configured` is the only read-side reflection of the secret.
 *   - Empty auth-key on save = "keep existing".
 *   - Provider switch is gated behind a "completeness check" on the backend.
 */
export function AdminWhatsappIntegrationsPage() {
  const { admin, loading } = useAdminAuth();
  const [config, setConfig] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [savingForm, setSavingForm] = useState(false);
  const [savingProvider, setSavingProvider] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  // Form state
  const [authKey, setAuthKey] = useState('');
  const [showAuthKey, setShowAuthKey] = useState(false);
  const [senderNumber, setSenderNumber] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateLang, setTemplateLang] = useState('en');
  const [testRecipient, setTestRecipient] = useState('');

  // Email-only OTP guard (Part C).
  const { guard, dialog: otpDialog } = useAdminOtpGuard();

  const refresh = async () => {
    setFetching(true);
    try {
      const r = await adminApi.get('/admin/whatsapp-integrations');
      setConfig(r.data);
      const cfg = r.data.msg91 || {};
      setSenderNumber(cfg.sender_number || '');
      setTemplateName(cfg.template_name || '');
      setTemplateLang(cfg.template_language || 'en');
      // Never pre-fill auth_key — it's write-only.
      setAuthKey('');
    } catch {
      toast.error('Could not load WhatsApp integration config.');
    } finally {
      setFetching(false);
    }
  };

  useEffect(() => { if (admin) refresh(); }, [admin]);

  if (loading) return null;
  if (!admin) return <Navigate to="/admin/login" replace />;

  const saveMsg91 = async () => {
    if (!senderNumber || !templateName || !templateLang) {
      return toast.error('Sender number, template name, and language are required.');
    }
    if (!config?.msg91?.is_auth_key_configured && !authKey) {
      return toast.error('Auth key is required for first-time setup.');
    }
    await guard({
      requestOtpUrl: '/admin/whatsapp-integrations/request-otp',
      title: 'Confirm MSG91 save',
      description: 'Saving WhatsApp MSG91 credentials needs Email OTP verification.',
      performSave: async (headers) => {
        setSavingForm(true);
        try {
          const r = await adminApi.put('/admin/whatsapp-integrations/msg91', {
            auth_key: authKey || null,
            sender_number: senderNumber.trim(),
            template_name: templateName.trim(),
            template_language: templateLang.trim(),
          }, { headers });
          setConfig(r.data);
          setAuthKey('');
          toast.success('MSG91 configuration saved.');
        } finally {
          setSavingForm(false);
        }
      },
    });
  };

  const switchProvider = async (next) => {
    await guard({
      requestOtpUrl: '/admin/whatsapp-integrations/request-otp',
      title: 'Confirm provider switch',
      description: `Switching to ${next === 'msg91' ? 'live MSG91' : 'mock'} mode needs Email OTP verification.`,
      performSave: async (headers) => {
        setSavingProvider(true);
        try {
          const r = await adminApi.put('/admin/whatsapp-integrations/provider', { provider: next }, { headers });
          setConfig(r.data);
          toast.success(next === 'msg91' ? 'Switched to MSG91 — WhatsApp OTPs now go live.' : 'Reverted to mock mode.');
        } finally {
          setSavingProvider(false);
        }
      },
    });
  };

  const sendTest = async () => {
    if (!testRecipient || !testRecipient.match(/^\+?[0-9]{10,15}$/)) {
      return toast.error('Enter a valid international number (e.g. +919876543210).');
    }
    setSendingTest(true);
    try {
      const r = await adminApi.post('/admin/whatsapp-integrations/test', { recipient: testRecipient });
      // The endpoint now returns 200 with {ok, message|error} so we can
      // surface MSG91-side rejections cleanly instead of being eaten by
      // the upstream CDN's own 5xx error page.
      if (r.data?.ok === false) {
        toast.error(r.data?.error || 'Test send failed.');
      } else {
        toast.success(r.data?.message || 'Test OTP sent.');
      }
    } catch (e) {
      const d = e?.response?.data?.detail || e?.response?.data?.error;
      toast.error(typeof d === 'string' ? d : 'Test send failed.');
    } finally {
      setSendingTest(false);
    }
  };

  const provider = config?.provider || 'mock';
  const live = provider === 'msg91';
  const hasKey = !!config?.msg91?.is_auth_key_configured;

  return (
    <div
      data-testid="admin-whatsapp-integrations-page"
      className="min-h-screen"
      style={{ background: 'radial-gradient(circle at 0% 0%, #1e293b 0%, #0f172a 50%, #020617 100%)' }}
    >
      <header className="border-b border-slate-800 bg-slate-950/60 backdrop-blur-xl">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/admin" className="w-9 h-9 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center hover:bg-slate-800" data-testid="whatsapp-int-back">
            <ArrowLeft className="w-4 h-4 text-slate-300" />
          </Link>
          <div className="w-9 h-9 rounded-lg bg-emerald-500/10 border border-emerald-500/30 flex items-center justify-center">
            <MessageSquare className="w-4 h-4 text-emerald-400" />
          </div>
          <div>
            <div className="text-white font-semibold">WhatsApp Integrations</div>
            <div className="text-xs text-slate-400">Configure MSG91 to send live OTPs to recovery numbers.</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-6 space-y-5">
        {fetching ? (
          <div className="flex items-center justify-center gap-2 text-slate-400 text-sm py-10">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            {/* Active provider banner */}
            <div
              data-testid="whatsapp-provider-banner"
              className={`rounded-xl border p-4 flex items-start gap-3 ${
                live ? 'bg-emerald-500/10 border-emerald-500/40' : 'bg-amber-500/10 border-amber-500/40'
              }`}
            >
              {live ? <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5" /> : <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5" />}
              <div className="flex-1">
                <div className={`text-sm font-semibold ${live ? 'text-emerald-300' : 'text-amber-300'}`}>
                  {live ? 'MSG91 — Live delivery active' : 'Mock mode — OTPs default to 123456'}
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  {live
                    ? 'Recovery WhatsApp OTPs are sent through MSG91. Switch back to mock for testing.'
                    : 'Configure MSG91 below, then flip the switch to make WhatsApp OTPs go live.'}
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => switchProvider(live ? 'mock' : 'msg91')}
                disabled={savingProvider || (!live && !hasKey)}
                data-testid="whatsapp-provider-toggle"
                className={live
                  ? 'bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700'
                  : 'bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold'}
              >
                {savingProvider ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : null}
                {live ? 'Switch to mock' : 'Go live'}
              </Button>
            </div>

            {/* MSG91 form */}
            <div data-testid="whatsapp-msg91-card" className="bg-slate-900/70 border border-slate-800 rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-white font-semibold">MSG91 credentials</h2>
                <span className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded border ${
                  hasKey ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300' : 'bg-slate-800 border-slate-700 text-slate-400'
                }`}>
                  {hasKey ? 'Auth key set' : 'Not configured'}
                </span>
              </div>

              <div>
                <label className="text-xs text-slate-300 uppercase tracking-wider mb-1.5 block">Auth key {hasKey && <span className="text-[10px] text-slate-500 normal-case tracking-normal">(leave blank to keep existing)</span>}</label>
                <div className="relative">
                  <Input
                    type={showAuthKey ? 'text' : 'password'}
                    value={authKey}
                    onChange={(e) => setAuthKey(e.target.value)}
                    placeholder={hasKey ? '••••••••••••••••••••' : 'Paste your MSG91 Auth Key'}
                    data-testid="whatsapp-msg91-auth-key"
                    className="pr-10 bg-slate-800/50 border-slate-700 text-white font-mono"
                  />
                  <button
                    type="button"
                    onClick={() => setShowAuthKey((v) => !v)}
                    data-testid="whatsapp-msg91-auth-key-toggle"
                    aria-label={showAuthKey ? 'Hide auth key' : 'Show auth key'}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-slate-400 hover:text-amber-400"
                  >
                    {showAuthKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-[11px] text-slate-500 mt-1">MSG91 Dashboard → Profile → Auth Key. Write-only — never re-displayed.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-slate-300 uppercase tracking-wider mb-1.5 block">Sender number</label>
                  <Input
                    value={senderNumber}
                    onChange={(e) => setSenderNumber(e.target.value.replace(/[^0-9]/g, '').slice(0, 15))}
                    placeholder="919876543210"
                    data-testid="whatsapp-msg91-sender"
                    className="bg-slate-800/50 border-slate-700 text-white font-mono"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">Country code + number, no &apos;+&apos;.</p>
                </div>

                <div>
                  <label className="text-xs text-slate-300 uppercase tracking-wider mb-1.5 block">Template language</label>
                  <Input
                    value={templateLang}
                    onChange={(e) => setTemplateLang(e.target.value)}
                    placeholder="en"
                    data-testid="whatsapp-msg91-lang"
                    className="bg-slate-800/50 border-slate-700 text-white font-mono"
                  />
                </div>
              </div>

              <div>
                <label className="text-xs text-slate-300 uppercase tracking-wider mb-1.5 block">Template name</label>
                <Input
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder="rupiwise_otp_auth"
                  data-testid="whatsapp-msg91-template"
                  className="bg-slate-800/50 border-slate-700 text-white font-mono"
                />
                <p className="text-[11px] text-slate-500 mt-1">Name of your Meta-approved Authentication template.</p>
              </div>

              <Button
                onClick={saveMsg91}
                disabled={savingForm}
                data-testid="whatsapp-msg91-save"
                className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
              >
                {savingForm ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save MSG91 config
              </Button>
            </div>

            {/* Test send */}
            <div data-testid="whatsapp-test-card" className="bg-slate-900/70 border border-slate-800 rounded-xl p-5 space-y-3">
              <h2 className="text-white font-semibold">Send a test OTP</h2>
              <p className="text-xs text-slate-400">Requires the active provider to be MSG91 and config complete.</p>
              <div className="flex gap-2">
                <Input
                  value={testRecipient}
                  onChange={(e) => setTestRecipient(e.target.value)}
                  placeholder="+919876543210"
                  data-testid="whatsapp-test-recipient"
                  className="flex-1 bg-slate-800/50 border-slate-700 text-white font-mono"
                />
                <Button
                  onClick={sendTest}
                  disabled={sendingTest || !live}
                  data-testid="whatsapp-test-send"
                  className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold"
                >
                  {sendingTest ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send test
                </Button>
              </div>
            </div>
          </>
        )}
      </main>
      {otpDialog}
    </div>
  );
}
